import { formatTime, toBerlinDateKey } from '@brimo/shared';
import { dayKeyToDate } from '../../calendar/dates';

/** Texts of the booking page composed on the client (Europe/Berlin). */

const TIME_ZONE = 'Europe/Berlin';
const weekdayFormat = new Intl.DateTimeFormat('de-DE', { weekday: 'short', timeZone: TIME_ZONE });
const dayMonthLong = new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'long', timeZone: TIME_ZONE });
const dayMonthShort = new Intl.DateTimeFormat('de-DE', {
  day: 'numeric',
  month: 'short',
  timeZone: TIME_ZONE,
});
const monthYear = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const monthOnly = new Intl.DateTimeFormat('de-DE', { month: 'long', timeZone: 'UTC' });

function weekday(value: string): string {
  return weekdayFormat.format(new Date(value)).replace('.', '');
}

/** „Do, 8. Oktober · 14:30–15:00" (Buchungsseite 02 „Ihr Termin"). */
export function slotLabel(startsAt: string, endsAt: string): string {
  return `${weekday(startsAt)}, ${dayMonthLong.format(new Date(startsAt))} · ${formatTime(startsAt)}–${formatTime(endsAt)}`;
}

/** „Do, 8. Okt · 16:00–16:30" (Buchungsseite 10, the mobile chip). */
export function slotLabelShort(startsAt: string, endsAt: string): string {
  const day = dayMonthShort.format(new Date(startsAt)).replace(/\.$/, '');
  return `${weekday(startsAt)}, ${day} · ${formatTime(startsAt)}–${formatTime(endsAt)}`;
}

/** „Oktober 2026" of a month key `YYYY-MM`. */
export function monthLabel(month: string): string {
  return monthYear.format(dayKeyToDate(`${month}-01`));
}

/** „Oktober" of a month key `YYYY-MM`. */
export function monthName(month: string): string {
  return monthOnly.format(dayKeyToDate(`${month}-01`));
}

/** `YYYY-MM` of a day key. */
export function monthOf(day: string): string {
  return day.slice(0, 7);
}

function daysBetween(from: string, to: string): number {
  return Math.round((dayKeyToDate(to).getTime() - dayKeyToDate(from).getTime()) / 86_400_000);
}

/**
 * „Hervorgehobene Tage haben freie Zeiten. Frühester Termin morgen, spätestens 4 Wochen im Voraus."
 * from the booking window of the page (`earliest`/`latest`).
 */
export function windowFootnote(
  earliest: string,
  latest: string,
  today = toBerlinDateKey(new Date()),
): string {
  const first = daysBetween(today, earliest);
  const last = daysBetween(today, latest);
  const firstText = first <= 0 ? 'heute' : first === 1 ? 'morgen' : `in ${first} Tagen`;
  const lastText =
    last % 7 === 0
      ? `${last / 7} ${last === 7 ? 'Woche' : 'Wochen'}`
      : `${last} ${last === 1 ? 'Tag' : 'Tage'}`;
  return `Hervorgehobene Tage haben freie Zeiten. Frühester Termin ${firstText}, spätestens ${lastText} im Voraus.`;
}

/** „3. November" from the API label „Dienstag, 3. November" (the button „Zum 3. November"). */
export function withoutWeekday(label: string): string {
  const index = label.indexOf(', ');
  return index === -1 ? label : label.slice(index + 2);
}

/**
 * The video-type `channelLine` prefixes of the public page: the DAMOKLES room („Video · Link per E-Mail") and the
 * static meeting links („Google Meet · Link per E-Mail", „Microsoft Teams · …", „Zoom · …").
 */
const VIDEO_SERVICES = ['Video', 'Google Meet', 'Microsoft Teams', 'Zoom'] as const;

/** „Video" / „Google Meet" / „Microsoft Teams" / „Zoom" for a video-type line, otherwise null. */
export function videoServiceOf(channelLine: string): string | null {
  return VIDEO_SERVICES.find((service) => channelLine.startsWith(service)) ?? null;
}

/**
 * „Video" / „Google Meet" / „Microsoft Teams" / „Zoom" / „Telefon" / „Vor Ort" — the short channel of the mobile
 * offer line („Jonas Weiler · 30 Min · Video").
 */
export function channelShort(channelLine: string): string {
  const service = videoServiceOf(channelLine);
  if (service) return service;
  if (channelLine.startsWith('Telefon')) return 'Telefon';
  return 'Vor Ort';
}

export function isVideo(channelLine: string): boolean {
  return videoServiceOf(channelLine) !== null;
}
